import supabase from "./supabase";
import { FunctionsHttpError } from "@supabase/supabase-js";

// Hidden login address given to students imported without an email (see supabase/functions/_shared/kidpin.ts).
// Staff screens must never show it: use displayEmail().
export const PLACEHOLDER_EMAIL_DOMAIN = "kids.pe.startupsmartup.com";

export function isPlaceholderEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith("@" + PLACEHOLDER_EMAIL_DOMAIN);
}

/** The email to show staff, or `fallback` for students who have none (class code + picture PIN login). */
export function displayEmail(email: string | null | undefined, fallback = "No email"): string {
  return !email || isPlaceholderEmail(email) ? fallback : email;
}

// Kid login talks to a public edge function with only the anon key (the kid is signed out).
const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kidLogin`;

export interface KidRosterEntry {
  id: string; // opaque credential id, not a user id
  label: string; // first name, with last initial or a number when two kids share a first name
}

export type KidLoginErrorCode =
  | "class_not_found"
  | "wrong_pin"
  | "locked"
  | "class_locked"
  | "hard_locked"
  | "too_many_tries"
  | "network"
  | "other";

export interface KidLoginResult<T> {
  data?: T;
  error?: KidLoginErrorCode;
  triesLeft?: number;
}

async function call(body: Record<string, unknown>): Promise<{ status: number; json: any }> {
  try {
    const res = await fetch(FN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_KEY}`,
      },
      body: JSON.stringify(body),
    });
    return { status: res.status, json: await res.json().catch(() => ({})) };
  } catch {
    return { status: 0, json: {} };
  }
}

function failure<T>(status: number, json: any): KidLoginResult<T> {
  if (status === 0) return { error: "network" };
  const e = json?.error;
  if (e === "class_not_found") return { error: "class_not_found" };
  if (e === "wrong_pin") return { error: "wrong_pin", triesLeft: json?.triesLeft };
  if (e === "locked") return { error: "locked" };
  if (e === "class_locked") return { error: "class_locked" };
  if (e === "hard_locked") return { error: "hard_locked" };
  if (e === "too_many_tries") return { error: "too_many_tries" };
  return { error: "other" };
}

export async function fetchKidRoster(classCode: string): Promise<KidLoginResult<KidRosterEntry[]>> {
  const { status, json } = await call({ action: "roster", classCode });
  if (status !== 200) return failure(status, json);
  return { data: json.students ?? [] };
}

/**
 * Verifies class code + student + PIN on the server. The server answers with a one-time random password for the
 * kid's hidden account (it changes at every PIN sign-in), and this browser signs in with it directly. Signing in
 * from the browser keeps Auth's per-IP limits per school instead of one shared edge-function IP (round 3, B3).
 * Nothing is stored or shown.
 */
export async function kidSignIn(
  classCode: string,
  credentialId: string,
  pin: number[]
): Promise<KidLoginResult<true>> {
  // If the browser sign-in fails right after the PIN was accepted (another device rotated the password a moment
  // earlier), run the whole PIN check + sign-in once more. Only once, so two devices can't keep kicking each other.
  for (let attempt = 0; attempt < 2; attempt++) {
    const { status, json } = await call({ action: "login", classCode, credentialId, pin });
    if (status !== 200 || !json.signIn?.email || !json.signIn?.password) return failure(status, json);
    const { error } = await supabase.auth.signInWithPassword({
      email: json.signIn.email,
      password: json.signIn.password,
    });
    if (!error) return { data: true };
    if (error.status === 429) return { error: "too_many_tries" };
  }
  return { error: "other" };
}

// ---- Teacher side (signed in as teacher) ----

export interface KidCardStudent {
  id: string;
  label: string;
  firstName: string;
  lastName: string;
  pin: number[];
  locked?: boolean; // hard lock after repeated wrong PINs: only Reset PIN or Unlock clears it
  wrongThisWeek?: number; // wrong picture tries in the last 7 days
  wrongCount?: number; // wrong tries counting toward the hard lock (30 days, since the last Reset PIN / Unlock)
}

export interface KidCardsData {
  className: string;
  classCode: string;
  students: KidCardStudent[];
  emailStudentCount?: number; // students with a real email: they use email + password, no card
  hardLockAt?: number; // wrong tries in 30 days that lock a card
}

async function invokeManage<T>(body: Record<string, unknown>): Promise<{ data?: T; error?: string }> {
  const { data, error } = await supabase.functions.invoke("manageKidPins", { body });
  if (error && error instanceof FunctionsHttpError) {
    const m = await error.context.json().catch(() => ({}));
    return { error: m?.error ?? error.message };
  }
  if (error) return { error: error.message };
  return { data: data as T };
}

export const getKidCards = (classId: string) => invokeManage<KidCardsData>({ action: "cards", classId });
export const resetKidPin = (classId: string, credentialId: string) =>
  invokeManage<{ id: string; pin: number[]; locked?: boolean; signedOut?: boolean }>({ action: "reset", classId, credentialId });
/** Same PIN, clears the lock and the wrong-try count (for a kid who just mistyped). */
export const unlockKidPin = (classId: string, credentialId: string) =>
  invokeManage<{ id: string; locked?: boolean }>({ action: "unlock", classId, credentialId });

/** Where a signed-out student goes: no-email (picture PIN) kids back to the kid login, others to the login portal. */
export function studentLogoutPath(email: string | null | undefined, classCode?: string | null): string {
  if (!isPlaceholderEmail(email)) return "/login-portal";
  return classCode ? `/kid-login?code=${encodeURIComponent(classCode)}` : "/kid-login";
}
